import React from "react";
import {
    InfoCircleOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    UserOutlined,
    LockOutlined,
    ApiOutlined,
    BellOutlined
} from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { capitalizeFirstLetter } from "../../../../utils/functions";
import SOURCES from "../../../../utils/constants";

const EnhancedLeadSourceDetails = ({
    leadSourceDetail,
    setShowDetail
}) => {
    if (!leadSourceDetail) return null;

    return (
        <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <GlobalOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Lead Source Details</span>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>

                    {/* Basic Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Basic Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Job Name", value: leadSourceDetail.jobName },
                                { label: "Lead Source", value: capitalizeFirstLetter(leadSourceDetail.leadSource) },
                                {
                                    label: "Status",
                                    value: leadSourceDetail.active ? 'Active' : 'Inactive',
                                    isTag: true,
                                    color: leadSourceDetail.active ? 'success' : 'error',
                                    icon: leadSourceDetail.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />
                                },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            {item.label}
                                        </span>
                                        {item.isTag ? (
                                            <Tag color={item.color} icon={item.icon} className="m-0 px-3 py-1 font-medium capitalize w-fit">
                                                {item.value || "—"}
                                            </Tag>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Credentials Section
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <LockOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Credentials</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Username/Email", value: leadSourceDetail.username },
                                { label: "Password", value: leadSourceDetail.password }, // Consider masking this or showing it based on permission
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            {item.label === "Username/Email" ? <UserOutlined /> : <LockOutlined />}
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col> */}

                    {/* Destinations Section */}
                    {leadSourceDetail.destinations && leadSourceDetail.destinations.length > 0 && (
                        <Col span={24} className="border-b border-gray-200 py-4">
                            <div className="flex items-center gap-2 mb-4">
                                <ApiOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                                <span style={{ fontSize: "15px", fontWeight: 600 }}>Destinations</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {leadSourceDetail.destinations.map((dest, index) => (
                                    <Tag key={index} color="blue" className="px-3 py-1 text-sm">
                                        {capitalizeFirstLetter(dest)}
                                    </Tag>
                                ))}
                            </div>
                            {/* Additional Info for Specific Destinations */}
                            {leadSourceDetail.extraInfo?.locationIdTripleseat && (
                                <div className="mt-2 text-sm text-gray-600">
                                    <span className="font-semibold">Tripleseat Location ID:</span> {leadSourceDetail.extraInfo.locationIdTripleseat}
                                </div>
                            )}
                            {leadSourceDetail.extraInfo?.campaignIdMailChimp && (
                                <div className="mt-2 text-sm text-gray-600">
                                    <span className="font-semibold">MailChimp Campaign ID:</span> {leadSourceDetail.extraInfo.campaignIdMailChimp}
                                </div>
                            )}
                        </Col>
                    )}

                    {/* Alert Configuration Section */}
                    {(leadSourceDetail.alertSendType || (leadSourceDetail.alertId && leadSourceDetail.alertId.length > 0)) && (
                        <Col span={24} className="pt-4">
                            <div className="flex items-center gap-2 mb-4">
                                <BellOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                                <span style={{ fontSize: "15px", fontWeight: 600 }}>Alert Configuration</span>
                            </div>
                            <Row gutter={[24, 16]}>
                                {leadSourceDetail.alertSendType && (
                                    <Col span={12}>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-500 mb-1">Send Type</span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {leadSourceDetail.alertSendType === 'specifictime' ? 'Specific Time' : 'Anytime'}
                                            </span>
                                        </div>
                                    </Col>
                                )}
                                {leadSourceDetail.alertSendType === 'specifictime' && (
                                    <>
                                        <Col span={12}>
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 mb-1">Window</span>
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {leadSourceDetail.alertTime?.startTime} - {leadSourceDetail.alertTime?.endTime}
                                                </span>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 mb-1">Timezone</span>
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {leadSourceDetail.alertTime?.timezone}
                                                </span>
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col span={24}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 mb-2">Alert Recipients</span>
                                        <div className="flex flex-wrap gap-2">
                                            {/* Note: In the list view we might only have IDs. 
                                                ideally we should have the names in the data or fetch them. 
                                                If we only have IDs, we might just show count or "Users". 
                                                Assuming data might have populated users or just IDs 
                                            */}
                                            {Array.isArray(leadSourceDetail.alertId) ? (
                                                leadSourceDetail.alertId.length > 0 ? (
                                                    <Tag>{leadSourceDetail.alertId.length} User(s) Notified</Tag>
                                                ) : <span className="text-sm text-gray-500">No users selected</span>
                                            ) : null}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    )}

                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedLeadSourceDetails;
